/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React from "react";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import {ErrorMessage, Field, useField} from "formik";
import {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {useCaseTransformOnChange} from "qqq/components/forms/useCaseTransformOnChange";

interface Props
{
   fieldName: string;
   formFieldDef: DynamicFormFieldDefinition;
   onBlur?: (fieldName: string, value: any) => void;
}

export const SimpleInputEditCell: React.FC<Props> = ({fieldName, formFieldDef, onBlur}) =>
{
   const [, meta] = useField(fieldName);
   const showError = meta.touched && !!meta.error;

   //////////////////////////////////////////////////////////////////////////////////
   // Case-behavior (toUpperCase / toLowerCase) — mirrors DynamicFormField on the  //
   // RecordScreen so behaviors attached to fields apply in row-edit too.          //
   //////////////////////////////////////////////////////////////////////////////////
   const {onChange} = useCaseTransformOnChange(fieldName, formFieldDef?.fieldMetaData);
   const changeProps = onChange ? {onChange} : {};

   return (
      <Box sx={{width: "100%", display: "flex", flexDirection: "column"}}>
         <Field
            name={fieldName}
            type={formFieldDef.type?.toLowerCase()}
            as={OutlinedInput}
            error={showError}
            size="small"
            fullWidth
            disabled={!formFieldDef.isEditable}
            onKeyDown={(e: React.KeyboardEvent) =>
            {
               ///////////////////////////////////////////////////////////////////////////////
               // Stop keydown from bubbling to the DataGrid.  MUI DataGrid captures Space, //
               // arrow keys, etc. for its own keyboard navigation — which, when a cell is  //
               // focused via our input, scrolls the row to the top or moves the selection. //
               // Tab (and Shift+Tab) are allowed to bubble so grid-level focus navigation  //
               // still works; their browser default (focus next/prev element) runs too.    //
               ///////////////////////////////////////////////////////////////////////////////
               if (e.key !== "Tab")
               {
                  e.stopPropagation();
               }
               if (e.key === "Enter")
               {
                  ///////////////////////////////////////////////////////////////////////////
                  // Per spec: Enter is a no-op in row edit (product rejected Enter=save). //
                  ///////////////////////////////////////////////////////////////////////////
                  e.preventDefault();
               }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur?.(fieldName, e.target.value)}
            {...changeProps}
         />
         {showError && (
            <Box className="qqq-row-edit-field-error" sx={{fontSize: "0.75rem", color: "error.main", mt: "2px", mb: "2px", lineHeight: 1.2, whiteSpace: "normal", wordBreak: "break-word"}}>
               <ErrorMessage name={fieldName} render={msg => <span data-field-error="true">{msg}</span>} />
            </Box>
         )}
      </Box>
   );
};
